<template>
  <div class="waves-container"
       :class="{'animate-red':color === 'redBlinking',
                'small':size === 'small'}">
    <canvas ref="waveCanvas"></canvas>
  </div>
</template>

<script>

import {mapState} from "vuex";

export default {
  name: "ProgressWaves",
  props: {
    width: {
      type: Number,
    },
    color: {
      type: String,
      validator: (val) => ['default','green', 'lightGreen', 'yellow', 'orange', 'red', 'redBlinking'].includes(val),
      default: 'default'
    },
    size: {
      type: String,
      default: 'default'
    },
    textDisplay: {
      type: String
    }
  },
  data() {
    return {
      animationId: null
    }
  },
  computed: {
    ...mapState('theme', ['theme'])
  },
  methods: {
    waveGradient(color) {
      const canvas = this.$refs.waveCanvas,
          ctx = canvas.getContext('2d');
      let gradient = ctx.createLinearGradient(0, 0, 105, 387);
      switch (color) {
        case 'green':
          gradient.addColorStop(.27, "#87FFA1");
          gradient.addColorStop(.92, "#62DE24");
          break;
        case 'lightGreen':
          gradient.addColorStop(.27, "#63DF24");
          gradient.addColorStop(.94, "#FFD600");
          break;
        case 'yellow':
          gradient.addColorStop(.27, "#FF7A00");
          gradient.addColorStop(.93, "#FFD701");
          break;
        case 'orange':
          gradient.addColorStop(.06, "#FF7A00");
          gradient.addColorStop(.72, "#FF003F");
          break;
        case 'red':
          gradient.addColorStop(.23, "#B00808");
          gradient.addColorStop(.93, "#E94040");
          break;
        case 'redBlinking':
          gradient.addColorStop(.23, "#FF0000");
          gradient.addColorStop(.93, "#FF013F");
          break;
        default:
          if (this.$store.state.theme.theme === 1) {
            gradient.addColorStop(.27, "#2D4C5E");
            gradient.addColorStop(.92, "#2D4C5E");
          } else {
            gradient.addColorStop(.27, "white");
            gradient.addColorStop(.92, "white");
          }
          break;
      }
      return gradient;
    },
    textColor() {
      switch (this.color) {
        case 'green':
          return '#87FFA1';
        case 'lightGreen':
          return '#63DF24';
        case 'yellow':
          return '#FF7A00';
        case 'orange':
          return '#FF7A00';
        case 'red':
          return '#B00808';
        case 'redBlinking':
          return '#FF0000';
        default:
          if (this.$store.state.theme.theme === 1) {
            return '#2D4C5E';
          } else {
            return 'white';
          }
      }
    },
    drawFrontWave(delta) {
      const canvas = this.$refs.waveCanvas,
          ctx = canvas.getContext('2d');

      //make wave constraints
      const waveHeight = this.width / 2;
      const waveWidth = canvas.width * this.width / 100;
      const randomTop = Math.abs(Math.pow(Math.sin(delta / 1000), 5)) * waveHeight + waveWidth;
      const randomTopConstraint = Math.abs(Math.pow(Math.sin((delta / 1000) + 20), 5)) * waveHeight + waveWidth;
      const randomBottom = Math.abs(Math.pow(Math.sin((delta / 1000) + 10), 5)) * waveHeight + waveWidth;
      const randomBottomConstraint = Math.abs(Math.pow(Math.sin((delta / 1000)), 5)) * waveHeight + waveWidth;

      //draw wave
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(randomTop, 0);
      ctx.bezierCurveTo(
          randomBottomConstraint, canvas.height / 3,
          randomTopConstraint, canvas.height / 3 * 2,
          randomBottom, canvas.height);

      ctx.lineTo(0, canvas.height);
      ctx.lineTo(0, 0);
      ctx.lineTo(randomTop, 0);
      ctx.closePath();

      //fill wave color
      ctx.fillStyle = this.waveGradient(this.color);
      ctx.fill();
    },
    drawBackWave(delta) {
      const canvas = this.$refs.waveCanvas,
          ctx = canvas.getContext('2d');

      //make wave constraints
      const waveHeight = this.width / 2;
      const waveWidth = canvas.width * this.width / 100;
      const randomTop = Math.abs(Math.pow(Math.sin((delta / 1000) + 10), 5)) * waveHeight + waveWidth;
      const randomTopConstraint = Math.abs(Math.pow(Math.sin((delta / 1000)), 5)) * waveHeight + waveWidth;
      const randomBottom = Math.abs(Math.pow(Math.sin(delta / 1000), 5)) * waveHeight + waveWidth;
      const randomBottomConstraint = Math.abs(Math.pow(Math.sin((delta / 1000) + 20), 5)) * waveHeight + waveWidth;

      //draw wave
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(randomTop, 0);
      ctx.bezierCurveTo(
          randomBottomConstraint, canvas.height / 3,
          randomTopConstraint, canvas.height / 3 * 2,
          randomBottom, canvas.height);

      ctx.lineTo(0, canvas.height);
      ctx.lineTo(0, 0);
      ctx.lineTo(randomTop, 0);
      ctx.closePath();

      //fill wave color
      ctx.fillStyle = this.waveGradient(this.color);
      ctx.filter = "brightness(75%)";
      ctx.fill();
      ctx.filter = "brightness(100%)";

    },
    drawPercentage() {
      const canvas = this.$refs.waveCanvas,
          ctx = canvas.getContext('2d');
      if (this.theme === 1) {
        ctx.globalCompositeOperation = 'xor';
      }
      let scale = window.devicePixelRatio;
      let fontSize;
      switch (this.size) {
        case 'small':
          fontSize = 15;
          break;
        default:
          fontSize = 36;
          break;
      }
      ctx.filter = "brightness(75%)";
      ctx.fillStyle = this.theme === 1 ? this.textColor() : 'white';
      ctx.font = `bold ${fontSize * scale}px Roboto`;
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle';
      if (this.textDisplay) {
        ctx.fillText(this.textDisplay, canvas.width / 2, canvas.height / 2);
      } else {
        ctx.fillText(`${Math.round(this.width)}%`, canvas.width / 2, canvas.height / 2);
      }
      ctx.filter = "brightness(100%)";
    },
    draw(delta) {
      const canvas = this.$refs.waveCanvas;
      this.animationId = requestAnimationFrame(this.draw);
      canvas.width = canvas.width;

      this.drawBackWave(delta)
      this.drawFrontWave(delta)
      this.drawPercentage()
    }
  },
  mounted() {
    const canvas = this.$refs.waveCanvas,
        ctx = canvas.getContext('2d');

    canvas.width = canvas.getBoundingClientRect().width;
    canvas.height = canvas.getBoundingClientRect().height;

    let scale = window.devicePixelRatio, // Change to 1 on retina screens to see blurry canvas.
        size_width = canvas.width,
        size_height = canvas.height;
    canvas.width = Math.floor(size_width * scale);
    canvas.height = Math.floor(size_height * scale);
    ctx.scale(scale, scale);

    this.animationId = requestAnimationFrame(this.draw);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.animationId)
  }
}
</script>

<style scoped lang="scss">

.waves-container {
  @apply w-full border border-border rounded relative overflow-hidden bg-block;
  height: 7rem;

  canvas {
    width: 100%;
    height: 100%;
  }

  &.small {
    height: 31px;
  }
}

.animate-red {
  animation: blinkRedOutline 2s infinite;
}

@keyframes blinkRedOutline {
  0%, 100% {
    box-shadow: 0px 2px 8px rgba(224, 64, 103, 0.6);
  }
  50% {
    box-shadow: 0px 4px 20px rgba(255, 40, 93, 0.9);
  }
}
</style>